<template>
  <div>
    <Layout v-if="isLoadingDoc" :show-upgrade-prompt-modal="false">
      <div class="layout__wrapper">
        <loading-spinner />
      </div>
    </Layout>

    <ArticleContent v-if="article" :key="article.id" :article="article" />

    <Layout
      v-if="!article && isErrorLoadingDoc"
      :show-upgrade-prompt-modal="false"
    >
      <div class="layout__wrapper">
        <LoadingError />
      </div>
    </Layout>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'

import { backend } from '@/services/backend'

import { Sentry } from '@/services/sentry'
import { assertNotNull } from '@/helpers/typing'

import { ArticleView } from '@/models/doc.article'
import { backLink } from '@/models/backlink'

import Layout from '@/app/components/Layout.vue'
import ArticleContent from '@/app/components/article/Content.vue'
import LoadingError from '@/components/ui/LoadingError.vue'

import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'

const route = useRoute()
const router = useRouter()
const wait = useWait()

const article = ref<Raw<ArticleView> | null>(null)
const isInitialLoad = ref(true)

onBeforeRouteUpdate(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // Called when the route that renders this component has changed,
    // but this component is reused in the new route.
    const articleValue = assertNotNull(article.value)
    await articleValue.initPage(to, from, loadDocument)
    backLink.value.updateMetaTitle(from, articleValue.title)

    next()
  },
)

onBeforeRouteLeave(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    const articleValue = assertNotNull(article.value)
    await articleValue.updateReadTimeAndSavePosition()

    next()
  },
)

onBeforeMount(async () => {
  wait.start('Loading Doc')
  await loadDocument(route)
})

async function _fetchDocument(
  route: RouteLocationNormalized,
): Promise<ArticleView> {
  const docData = await backend.getArticle(route.params.url_slug as string)
  const userData = await backend.getDocUserData(docData.id)
  return new ArticleView(docData, userData)
}

async function loadDocument(route: RouteLocationNormalized): Promise<void> {
  try {
    wait.start('Loading Doc')
    wait.end('Error Loading Doc')
    article.value = await _fetchDocument(route)
    await article.value.initDocument(isInitialLoad.value)
    // Don't restore the reading position in the next pages.
    isInitialLoad.value = false
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      wait.start('Error Loading Doc')
      Sentry.captureException(error)
    }
  } finally {
    wait.end('Loading Doc')
  }
}

const isLoadingDoc = computed(() => {
  return wait.is('Loading Doc')
})

const isErrorLoadingDoc = computed(() => {
  return wait.is('Error Loading Doc')
})
</script>
